// M3 storms, weather and sea state (M3_SPEC §6): seasonal storms per field environment, named hurricanes with a
// forecast and an evacuate / ride-it-out decision, historical storms (rules.HISTORICAL_STORMS), daily significant wave
// height (Hs) → waiting on weather, damage above survival limits, total loss, mooring failures; subsidence lowers
// platform design wave heights. State: state.fields[fid].sea { day, hsFt, wow } and .storm (mirrored on the region).
import { wellsOnField, allWells } from '../indexes.js';
import { chance, range, int, nextFloat } from '../../core/rng.js';
import { eqDef, fieldDef, stat } from '../defs.js';
import { STORMS, HISTORICAL_STORMS, stormHs, LOGISTICS } from '../../data/m3/rules.js';
import { m3, month, envOf, rigBuiltYear, envelope, ensureRushKinds } from './opsutil.js';
import { m3Accident, m3Mitigation } from './opsincidents.js';
import { notify, log, spend } from '../util.js';
import { hasUpgrade } from '../research.js';
import { queueDecision, popDecision } from '../security.js';
import { activeRigCrew } from '../crew.js';
import { startTimer } from '../timers.js';

const NAMES = ['Able', 'Baker', 'Carol', 'Dolly', 'Edna', 'Flora', 'Gracie', 'Hazel', 'Ione', 'Janet', 'Katie', 'Linda', 'Marge', 'Nancy', 'Opal', 'Pearl'];
export const STORM_AUTO_EVAC_DAYS = 1;

// Fields where the player has something exposed (wells, rigs, charters, platforms): only these roll weather.
export function exposedFields(state) {
  const out = new Set();
  for (const w of allWells(state)) if (w.offshore && !['PLUGGED', 'DRY'].includes(w.status)) out.add(w.fieldId);
  for (const p of Object.values(state.platforms ?? {})) if (p.status !== 'REMOVED') out.add(p.fieldId);
  return out;
}

function seasonSpecs(env, mon) {
  const out = [];
  if (env?.stormSeason?.months?.includes(mon) && (env.stormSeason.hsFt?.[1] ?? 0) > 0) out.push([env.stormSeason, env.stormSeason.named ? 'hurricane' : 'storm']);
  if (env?.winterStorms?.months?.includes(mon) && (env.winterStorms.hsFt?.[1] ?? 0) > 0) out.push([env.winterStorms, 'winter_storm']);
  return out;
}

export const warningDaysFor = (state, kind) => (kind === 'hurricane' ? STORMS.warningDays.hurricane : kind === 'winter_storm' ? STORMS.warningDays.winter : 0)
  + (hasUpgrade(state, 'metocean_forecasting') && kind !== 'storm' ? 1 : 0);

export const newStormDev = (...a) => newStorm(...a); // dev mode
function newStorm(state, fieldIds, { kind, hsFt, name = null, days, warn, historical = null, mudslides = false }) {
  const M = m3(state);
  const uid = `st${M.nextStorm++}`;
  const startDay = state.day + warn;
  const storm = { uid, kind, name, hsFt: Math.round(hsFt * 10) / 10, severity: Math.min(1, hsFt / 50), warnDay: state.day, startDay, untilDay: startDay + Math.max(1, days) - 1,
    decision: null, decisionId: null, fieldIds, historical, mudslides, damaged: [] };
  for (const fid of fieldIds) {
    const f = state.fields[fid];
    if (!f) continue;
    f.storm = storm;
    const rid = fieldDef(fid)?.regionId;
    if (rid && state.regions[rid]) state.regions[rid].storm = storm;
  }
  M.stormsSeen[uid] = { kind, name, day: startDay, hsFt: storm.hsFt, fieldIds };
  // a decision only for named storms, or when the forecast seas exceed a manned unit's survival / design limit
  const limitOf = (u) => (u.kind === 'platform' ? stat(eqDef(u.platform.defId), 'designHsFt', 40) : stat(eqDef(u.rig?.defId ?? u.well.contract?.rigDefId), 'survivalHsFt', 40));
  const manned = fieldIds.filter((fid) => mannedAt(state, fid).some((u) => kind === 'hurricane' || hsFt > limitOf(u)));
  const label = name ?? (kind === 'winter_storm' ? 'A winter gale' : 'A storm');
  if (warn > 0 && manned.length) {
    const id = `storm_${uid}`;
    storm.decisionId = id;
    queueDecision(state, {
      kind: 'storm', id, stormUid: uid, day: state.day, deadlineDay: state.day + STORM_AUTO_EVAC_DAYS, fieldIds: manned,
      title: `${label} — ${warn} day${warn > 1 ? 's' : ''} out`,
      text: `${label} is forecast to bring ${Math.round(storm.hsFt)}-ft seas to ${manned.map((f) => fieldDef(f)?.name).join(', ')}. Evacuate (shut in, fly the crews off) or ride it out (keep drilling and producing, full risk)?`,
      options: [{ choice: 'evacuate', label: 'Evacuate and shut in' }, { choice: 'ride', label: 'Ride it out' }],
    });
  }
  notify(state, 'storm', { fieldIds, hsFt: storm.hsFt, name, kind, warningDays: warn, decisionId: storm.decisionId, phase: 'warning', pause: warn > 0 && manned.length > 0 && !!state.settings?.autoPause?.storm,
    level: kind === 'hurricane' ? 'major' : 'warn', text: `${label}: ${Math.round(storm.hsFt)}-ft seas expected${warn > 0 ? ` in ${warn} day${warn > 1 ? 's' : ''}` : ' now'} at ${fieldIds.map((f) => fieldDef(f)?.name).filter(Boolean).join(', ')}.` });
  return storm;
}

// Manned units at a field: own rigs on wells, contractor charter wells, manned platforms.
export function mannedAt(state, fid) {
  const out = [];
  for (const r of Object.values(state.rigs)) { const w = r.wellId ? state.wells[r.wellId] : null; if (w && w.fieldId === fid && w.offshore) out.push({ kind: 'rig', rig: r, well: w }); }
  for (const w of wellsOnField(state, fid)) if (w.offshore && w.contract?.charter && !w.contract.doneDay) out.push({ kind: 'charter', well: w });
  for (const p of Object.values(state.platforms ?? {})) if (p.fieldId === fid && p.status !== 'BUILDING' && p.status !== 'REMOVED' && stat(eqDef(p.defId), 'manned', false)) out.push({ kind: 'platform', platform: p });
  return out;
}

export function resolveStormDecision(state, d, choice) {
  const storm = findStorm(state, d.stormUid);
  popDecision(state, d.id);
  if (!storm) return { ok: true, msg: 'The storm has passed.' };
  storm.decision = choice === 'ride' ? 'ride' : 'evacuate';
  if (storm.decision === 'evacuate') evacuate(state, storm);
  return { ok: true, msg: storm.decision === 'evacuate' ? `Evacuating ahead of ${storm.name ?? 'the storm'}: wells shut in, crews ashore.` : `Riding out ${storm.name ?? 'the storm'}. Batten down.` };
}

function findStorm(state, uid) {
  for (const f of Object.values(state.fields)) if (f.storm?.uid === uid) return f.storm;
  return null;
}

function evacuate(state, storm) {
  const until = storm.untilDay + STORMS.shutInDays;
  let n = 0;
  for (const fid of storm.fieldIds) {
    for (const w of allWells(state)) {
      if (w.fieldId !== fid || !w.offshore) continue;
      w.evacuated = until;
      if (w.prod) w.prod.forcedUntil = Math.max(w.prod.forcedUntil ?? 0, until);
      n++;
    }
  }
  state.company.stats.evacuations = (state.company.stats.evacuations ?? 0) + 1;
  log(state, `Evacuated ${n} offshore well${n === 1 ? '' : 's'} ahead of ${storm.name ?? 'the storm'}.`);
}

// Daily: historical + seasonal storms, sea state, storm effects, decision auto-resolution, clean-up.
export function tickStorms(state) {
  ensureRushKinds();
  const mon = month(state);
  // historical storms (named; forecast lead applies)
  const hWarn = warningDaysFor(state, 'hurricane'); // (perf: hoisted — pure, was evaluated per storm per day)
  for (const hs of HISTORICAL_STORMS) {
    const warn = hWarn;
    if (hs.day - warn !== state.day) continue;
    const fids = hs.fieldIds.filter((f) => state.fields[f] && fieldDef(f));
    if (fids.length) newStorm(state, fids, { kind: /North Sea|storm/i.test(hs.name) && !/Hurricane/.test(hs.name) ? 'winter_storm' : 'hurricane', hsFt: hs.hsFt, name: hs.name, days: int(state, 2, 4), warn, historical: hs.id, mudslides: !!hs.mudslides });
  }
  const exposed = exposedFields(state);
  for (const fid of exposed) {
    const f = state.fields[fid];
    const env = envOf(fid);
    if (!f || !env) continue;
    if (f.storm && state.day > f.storm.untilDay) endStorm(state, f);
    if (!f.storm) {
      for (const [spec, kind] of seasonSpecs(env, mon)) {
        const p = (spec.perYear ?? 0) / (30 * spec.months.length) * (spec.peakMonth === mon ? 1.5 : 1);
        if (!chance(state, p)) continue;
        const hsFt = stormHs(spec.hsFt, nextFloat(state));
        const named = kind === 'hurricane' && hsFt >= 20;
        const k = named ? 'hurricane' : kind === 'hurricane' ? 'storm' : kind;
        const days = k === 'hurricane' ? int(state, 2, 4) : int(state, 1, 3);
        const name = k === 'hurricane' ? `Hurricane ${NAMES[(Object.keys(m3(state).stormsSeen).length) % NAMES.length]}` : null;
        newStorm(state, [fid], { kind: k, hsFt, name, days, warn: warningDaysFor(state, k) });
        break;
      }
    }
    // sea state today
    const st = f.storm && state.day >= f.storm.startDay && state.day <= f.storm.untilDay ? f.storm : null;
    let hs;
    if (st) {
      const mid = (st.startDay + st.untilDay) / 2;
      hs = st.hsFt * (Math.abs(state.day - mid) < 0.6 || st.startDay === st.untilDay ? 1 : 0.75);
    } else {
      const winter = env.winterStorms?.months?.includes(mon);
      const base = winter ? (env.winterStorms.hsFt?.[0] ?? 8) * 0.45 : env.kind === 'harsh' ? 6 : env.kind === 'lake' || env.kind === 'swamp' ? 1 : 3;
      hs = base * (0.7 + 0.6 * frac(Math.sin((state.day + fid.length * 31) * 12.9898) * 43758.5453));
    }
    f.sea = { day: state.day, hsFt: Math.round(hs * 10) / 10, wow: false };
    if (st) stormDay(state, f, st, hs);
  }
  // auto-evacuate unanswered storm decisions
  for (const d of [state.pendingDecision, ...(state.decisionQueue ?? [])]) {
    if (d?.kind === 'storm' && state.day >= d.deadlineDay) resolveStormDecision(state, d, 'evacuate');
  }
  // expired evacuations
  for (const w of allWells(state)) if (w.evacuated && state.day > w.evacuated) w.evacuated = false;
}

const frac = (x) => x - Math.floor(x);

function endStorm(state, f) {
  const st = f.storm;
  f.storm = null;
  const rid = fieldDef(f.id)?.regionId;
  if (rid && state.regions[rid]?.storm?.uid === st.uid) state.regions[rid].storm = null;
  if (st.decision === 'ride' && st.fieldIds[0] === f.id && st.hsFt > 15 && !st.damaged.length) {
    state.company.stats.stormsRiddenOut = (state.company.stats.stormsRiddenOut ?? 0) + 1;
  }
  if (st.fieldIds[0] === f.id) notify(state, 'storm', { fieldIds: st.fieldIds, hsFt: st.hsFt, name: st.name, kind: st.kind, warningDays: 0, phase: 'passed', damaged: st.damaged.length, text: `${st.name ?? 'The storm'} has passed${st.damaged.length ? ` — ${st.damaged.length} unit${st.damaged.length > 1 ? 's' : ''} damaged` : ''}.`, level: 'info' });
}

const damageMult = (state) => (hasUpgrade(state, 'hurricane_planning') ? 0.8 : 1);

function stormDay(state, f, st, hs) {
  const D = STORMS.damage;
  f.sea.wow = true;
  const evacuated = st.decision === 'evacuate';
  // rigs (own) on offshore wells at this field
  for (const rig of Object.values(state.rigs)) {
    const w = rig.wellId ? state.wells[rig.wellId] : null;
    if (!w || w.fieldId !== f.id || !w.offshore) continue;
    const def = eqDef(rig.defId);
    const e = envelope(def, rigBuiltYear(rig));
    // moored floaters drift off location (riser disconnect)
    if (e.stationKeeping === 'moored' && hs > e.opLimitHsFt && chance(state, Math.min(0.6, D.mooringFailPerFtOver * (hs - e.opLimitHsFt)))) {
      w.mooringUntil = state.day + int(state, 3, 10);
      m3Accident(state, 'mooring_failure', { rig, well: w, crew: [], text: `Anchor lines parted in ${Math.round(hs)}-ft seas; emergency riser disconnect. Back on location in ${w.mooringUntil - state.day} days.`, pause: false });
    }
    if (hs <= e.survivalHsFt) continue;
    const over = hs - e.survivalHsFt;
    const noBallast = e.floating && !activeRigCrew(state, rig).some((c) => c.role === 'ballast_control_op');
    const lossOver = over - D.lossThresholdFt;
    if (lossOver > 0 && chance(state, Math.min(0.9, D.lossChancePerFtOver * lossOver * (noBallast ? 3 : 1) * damageMult(state)))) { loseRig(state, rig, w, st, hs, evacuated); continue; }
    if (chance(state, Math.min(0.95, D.perFtOver * over * (noBallast ? 1.5 : 1) * damageMult(state)))) {
      const dmg = range(state, 30, 80);
      rig.condition = Math.max(0, rig.condition - dmg);
      st.damaged.push(rig.uid);
      m3Accident(state, 'storm_damage', { rig, well: w, crew: evacuated ? [] : null, injured: evacuated ? 0 : int(state, 0, 2), text: `${Math.round(hs)}-ft seas tore into the rig (−${Math.round(dmg)}% condition).`, pause: true, extra: { press: 'hurricane', stormName: st.name } });
    }
  }
  // charter wells: the contractor's problem, but the well loses days
  // platforms
  for (const p of Object.values(state.platforms ?? {})) {
    if (p.fieldId !== f.id || p.status !== 'INSTALLED') continue;
    const ps = eqDef(p.defId)?.stats ?? {};
    if (ps.subsea) continue;
    const design = (ps.designHsFt ?? 40) - subsidenceFt(state, f.id);
    if (hs <= design) continue;
    if (chance(state, Math.min(0.95, D.perFtOver * (hs - design) * damageMult(state) * (st.mudslides ? 2 : 1)))) damagePlatform(state, p, hs, st, evacuated);
  }
}

// Ekofisk-style seabed subsidence: 0.1 ft per 1% depletion of compacting reservoirs in the field.
export function subsidenceFt(state, fid) {
  const fdef = fieldDef(fid);
  if (!fdef?.environment?.subsidence) return 0;
  let worst = 0;
  for (const p of fdef.prospects ?? []) for (const r of p.reservoirs ?? []) {
    if (!r.compacting) continue;
    const rs = state.fields[fid].reservoirs[r.id];
    if (rs?.initial > 0) worst = Math.max(worst, 100 * (1 - rs.remaining / rs.initial));
  }
  return worst * 0.1;
}

function loseRig(state, rig, well, st, hs, evacuated) {
  const crew = activeRigCrew(state, rig);
  let deaths = 0;
  if (!evacuated && crew.length) {
    const standby = Object.values(state.logistics ?? {}).some((l) => l.assignedTo?.id === rig.uid && stat(eqDef(l.defId), 'service', '') === 'standby');
    const share = range(state, STORMS.deathsIfNotEvacuated[0], STORMS.deathsIfNotEvacuated[1]) * (1 - m3Mitigation(state, rig, 'capsize')) * (standby ? 1 / LOGISTICS.noStandbyRescueMult : 1);
    deaths = Math.round(crew.length * share);
  }
  st.damaged.push(rig.uid);
  m3Accident(state, eqDef(rig.defId)?.stats?.floating ? 'capsize' : 'abandonment', { rig, well, crew, deaths, text: `${rig.name} was lost in ${Math.round(hs)}-ft seas${evacuated ? ' — the crew had been flown off' : ''}.`, pause: true, extra: { press: deaths > 0 ? 'platformDisaster' : 'hurricane', lost: true, stormName: st.name } });
  // the rig is gone: well suspended, gear lost, survivors to the bench
  well.rigId = null;
  well.status = ['PRODUCING', 'SHUT_IN'].includes(well.status) ? well.status : 'SUSPENDED';
  well.timerId = null;
  for (const t of Object.values(state.timers)) if (t.refId === well.id && t.kind !== 'RESEARCH') delete state.timers[t.id];
  for (const uid of [rig.engineUid, rig.bitUid, rig.bopUid, ...Object.values(rig.gear ?? {})]) if (uid) delete state.items[uid];
  for (const id of rig.crewIds) if (state.crew[id]) state.crew[id].rigId = null;
  for (const l of Object.values(state.logistics ?? {})) if (l.assignedTo?.id === rig.uid) l.assignedTo = null;
  for (const p of Object.values(state.platforms ?? {})) p.rigUids = (p.rigUids ?? []).filter((u) => u !== rig.uid);
  delete state.rigs[rig.uid];
  state.company.stats.rigsLost = (state.company.stats.rigsLost ?? 0) + 1;
}

function damagePlatform(state, p, hs, st, evacuated) {
  const def = eqDef(p.defId);
  const dmg = range(state, 30, 80);
  p.condition = Math.max(0, (p.condition ?? 100) - dmg);
  p.status = 'DAMAGED';
  st.damaged.push(p.uid);
  const days = Math.round(30 + dmg);
  const cost = (p.price ?? 0) * 0.1 * dmg / 55;
  spend(state, cost, 'other', false);
  ensureRushKinds();
  p.timerId = startTimer(state, 'PLATFORM_REPAIR', p.uid, days, { platformUid: p.uid }).id;
  for (const w of allWells(state)) if (w.platformUid === p.uid && w.prod) w.prod.forcedUntil = Math.max(w.prod.forcedUntil ?? 0, state.day + days);
  notify(state, 'platform', { platformUid: p.uid, fieldId: p.fieldId, phase: 'damaged', hsFt: hs, press: 'hurricane', stormName: st.name, pause: true, level: 'major',
    text: `${st.name ?? 'The storm'} damaged the ${def?.name ?? 'platform'} at ${fieldDef(p.fieldId)?.name} (${Math.round(hs)}-ft seas, −${Math.round(dmg)}%). Repairs: ${days} days.` });
  if (!evacuated && stat(def, 'manned', false) && hs > (def.stats.designHsFt ?? 40) + STORMS.damage.lossThresholdFt) {
    // manned platform overwhelmed: deaths among any crew on rigs standing on it
    for (const u of p.rigUids ?? []) { const r = state.rigs[u]; if (r) m3Accident(state, 'abandonment', { rig: r, deaths: Math.round(activeRigCrew(state, r).length * range(state, 0.05, 0.4) * (1 - m3Mitigation(state, r, 'abandonment'))), text: 'The platform crew had to abandon the installation.', extra: { press: 'platformDisaster' } }); }
  }
}
